import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowsClockwise, Pause, Play, Warning, Spinner, Lightning, Lock } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  action_plan:      { label: "Action Plan",       cls: "bg-purple-100 text-purple-800",  icon: Lightning },
  simulation:       { label: "Simulazione",       cls: "bg-blue-100 text-blue-800",      icon: Spinner },
  report_employee:  { label: "Report Dipendente", cls: "bg-emerald-100 text-emerald-800",icon: Spinner },
  report_candidate: { label: "Report Candidato",  cls: "bg-amber-100 text-amber-800",    icon: Spinner },
};

const fmtElapsed = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}m ${ss}s`;
};

export default function AdminGenerationsPage() {
  const [data, setData] = useState({ items: [], in_progress: 0, stale: 0 });
  const [typeFilter, setTypeFilter] = useState("all");
  const [onlyStale, setOnlyStale] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [releasing, setReleasing] = useState({});
  const lastFetch = useRef(0);

  const load = async () => {
    try {
      const params = {};
      if (typeFilter && typeFilter !== "all") params.type_filter = typeFilter;
      if (onlyStale) params.only_stale = "true";
      const r = await axios.get(`${API}/admin/generation-locks`, { params, withCredentials: true });
      setData(r.data || { items: [], in_progress: 0, stale: 0 });
      lastFetch.current = Date.now();
    } catch (e) {
      // silent: don't spam toast on auto-refresh
      if (e.response?.status === 401) toast.error("Sessione scaduta");
    }
  };

  useEffect(() => { load(); }, [typeFilter, onlyStale]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [autoRefresh, typeFilter, onlyStale]);

  const release = async (item) => {
    if (!window.confirm(
      `Forzare il release del lock?\n\nTipo: ${TYPE_META[item.type]?.label || item.type}\nWorkspace: ${item.workspace_name}\nEntità: ${item.entity_label}\n\nL'utente vedrà la generazione segnata come fallita. Operazione irreversibile.`
    )) return;
    setReleasing(p => ({ ...p, [item.lock_id]: true }));
    try {
      const r = await axios.post(
        `${API}/admin/generation-locks/${item.type}/${item.lock_id}/release`,
        {},
        { withCredentials: true }
      );
      if (r.data?.released) toast.success("Lock liberato");
      else toast.info("Lock già completato — nessuna azione necessaria");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore release");
    } finally {
      setReleasing(p => ({ ...p, [item.lock_id]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-generations-page">
      <PageHeader
        title="Stato Generazioni in Corso"
        subtitle="Monitor real-time delle elaborazioni async (action plan, simulazioni, report). Force release per sblocchi manuali."
      />

      {/* Batch 61.A — Test alert email (deliverability check) */}
      <TestAlertEmailCard />

      {/* Counter + Filters */}
      <Card className="p-4 border-[#0D1F3C]/8">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="counter-in-progress">
              <Spinner size={12} className="mr-1 animate-spin" /> {data.in_progress} in corso
            </Badge>
            <Badge className="bg-red-100 text-red-800 border-red-200" data-testid="counter-stale">
              <Warning size={12} className="mr-1" /> {data.stale} stale
            </Badge>
          </div>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[200px] h-9" data-testid="type-filter-trigger">
                <SelectValue placeholder="Tutti i tipi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                <SelectItem value="action_plan">Action Plan</SelectItem>
                <SelectItem value="simulation">Simulazione</SelectItem>
                <SelectItem value="report_employee">Report Dipendente</SelectItem>
                <SelectItem value="report_candidate">Report Candidato</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-xs text-[#0D1F3C]/70">
              <Switch checked={onlyStale} onCheckedChange={setOnlyStale} data-testid="only-stale-toggle" /> Solo stale
            </label>
            <label className="flex items-center gap-2 text-xs text-[#0D1F3C]/70">
              <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} data-testid="auto-refresh-toggle" />
              {autoRefresh ? <Play size={12} weight="fill" /> : <Pause size={12} weight="fill" />}
              Auto-refresh 5s
            </label>
            <Button variant="outline" size="sm" onClick={load} data-testid="manual-refresh-btn">
              <ArrowsClockwise size={14} className="mr-1" /> Aggiorna
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="border-[#0D1F3C]/8">
        {data.items.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-state">
            <Lock size={32} weight="duotone" className="mx-auto text-[#0D1F3C]/20 mb-3" />
            <p className="text-sm font-medium text-[#0D1F3C]">Nessuna elaborazione in corso. Tutto sereno.</p>
            <p className="text-xs text-[#0D1F3C]/40 mt-1">L'elenco si aggiorna automaticamente ogni 5 secondi.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Tipo</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Workspace</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Entità</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Started</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Elapsed</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Stato</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map(item => {
                const meta = TYPE_META[item.type] || { label: item.type, cls: "bg-gray-100 text-gray-700" };
                const Icon = meta.icon || Spinner;
                const isStale = item.status === "stale";
                return (
                  <TableRow key={`${item.type}-${item.lock_id}`} data-testid={`lock-row-${item.lock_id}`}>
                    <TableCell>
                      <Badge className={`${meta.cls} text-xs`}>
                        <Icon size={11} className="mr-1" />
                        {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-medium text-[#0D1F3C]">{item.workspace_name}</TableCell>
                    <TableCell className="text-sm text-[#0D1F3C]/70">{item.entity_label}</TableCell>
                    <TableCell className="text-xs text-[#0D1F3C]/50 font-mono">
                      {item.started_at ? new Date(item.started_at).toLocaleString("it-IT") : "—"}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-[#0D1F3C]/70">{fmtElapsed(item.elapsed_seconds)}</TableCell>
                    <TableCell>
                      {isStale ? (
                        <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">
                          <Warning size={11} className="mr-1" /> Stale
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs">
                          <Spinner size={11} className="mr-1 animate-spin" /> In corso
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!!releasing[item.lock_id]}
                        onClick={() => release(item)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        data-testid={`release-btn-${item.lock_id}`}
                      >
                        {releasing[item.lock_id] ? (
                          <><Spinner size={12} className="mr-1 animate-spin" /> Release…</>
                        ) : (
                          <><Lock size={12} className="mr-1" /> Force release</>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}


// Batch 61.A — Test alert email card
function TestAlertEmailCard() {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!to || !to.includes("@")) { toast.error("Email non valida"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/test-alert-email`, { to: to.trim() }, { withCredentials: true });
      toast.success(`Email inviata a ${to.trim()} — controlla anche la cartella spam`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-4 border-[#0D1F3C]/8" data-testid="test-alert-email-card">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <h3 className="text-sm font-semibold text-[#0D1F3C]">Test alert email</h3>
          <p className="text-xs text-[#0D1F3C]/50 mt-0.5">Verifica deliverability Resend con dominio <code>selly-hr.it</code>. Stesso template del C16 stale-lock alert.</p>
        </div>
        <input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="email@destinatario.it"
          className="h-9 px-3 rounded-md border border-[#0D1F3C]/15 text-sm w-[260px]"
          data-testid="test-email-input"
        />
        <Button onClick={send} disabled={busy} className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="test-email-send-btn">
          {busy ? "Invio…" : "Invia test"}
        </Button>
      </div>
    </Card>
  );
}
